export const MAX_STRUCTURED_CSV_SIZE = 2 * 1024 * 1024;

export interface StructuredCsvResult {
  headers: string[];
  records: Array<Record<string, string>>;
}

/**
 * RFC-4180 row tokenizer: quoted values may hold commas and newlines, `""` is
 * a literal quote, and both CRLF and LF end a row. Cells are trimmed.
 *
 * Shared with the lenient MATRIX parser (matrix-csv.ts), which is why
 * `keepEmptyRows` exists: the record-shaped parser drops fully-empty rows
 * (a blank line between records carries no record), while a matrix is
 * positional — a blank row inside it is a real, empty table row.
 */
export const parseCsvRows = (
  input: string,
  keepEmptyRows = false,
): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  const endRow = () => {
    row.push(cell.trim());
    if (keepEmptyRows || row.some(Boolean)) rows.push(row);
    row = [];
    cell = "";
  };

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    if (character === '"') {
      if (quoted && input[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && input[index + 1] === "\n") index += 1;
      endRow();
    } else {
      cell += character;
    }
  }
  if (quoted) throw new Error("CSV contains an unclosed quoted value");
  // A terminal newline ends the last row; it does not start another one. Only
  // matters when empty rows are kept — the record parser drops it anyway.
  const endsAfterNewline = row.length === 0 && cell === "" && rows.length > 0;
  if (!(keepEmptyRows && endsAfterNewline)) endRow();
  return rows;
};

export const parseStructuredCsv = (input: string): StructuredCsvResult => {
  const rows = parseCsvRows(input.replace(/^\uFEFF/, ""));
  if (rows.length < 2) {
    throw new Error("CSV must contain a header and at least one data row");
  }
  const headers = rows[0].map((header) => header.trim());
  if (headers.some((header) => !header)) {
    throw new Error("CSV column names cannot be empty");
  }
  const duplicate = headers.find(
    (header, index) => headers.indexOf(header) !== index,
  );
  if (duplicate) throw new Error(`Duplicate CSV column: ${duplicate}`);

  const records = rows.slice(1).map((values, index) => {
    if (values.length !== headers.length) {
      const count = values.length;
      throw new Error(
        `Row ${index + 2} has ${count} column${count === 1 ? "" : "s"}; expected ${headers.length}`,
      );
    }
    return Object.fromEntries(
      headers.map((header, column) => [header, values[column]]),
    );
  });
  return { headers, records };
};

export const readStructuredCsvFile = async (
  file: File,
): Promise<StructuredCsvResult> => {
  if (file.size > MAX_STRUCTURED_CSV_SIZE) {
    throw new Error("CSV file must be smaller than 2 MB");
  }
  return parseStructuredCsv(await file.text());
};
